import { GoogleMap, Polygon, useJsApiLoader } from '@react-google-maps/api';
import { useCallback, useEffect, useMemo, useState } from 'react';
import './App.css';
import AddressFormTailwind from './components/AddressFormTailwind';
import SidePanelTailwind from './components/SidePanelTailwind';
import { comunaDisplayName } from './config/comunas';
import type { Address, FiberData, SegmentFeature } from './types';

const mapContainerStyle = {
  width: '100%',
  height: '100vh'
};

const defaultCenter = {
  lat: -33.4618,
  lng: -70.7026
};

// Tamaño de celda de agregación: ~400m (≈4 cuadras) para ocultar
// la traza específica de calles por las que pasan los cables.
const CELL_SIZE_LAT = 0.0036;
const CELL_SIZE_LNG = 0.0043;

interface CoverageCell {
  key: string;
  paths: { lat: number; lng: number }[];
  segments: SegmentFeature[];
}

const aggregateSegmentsToCells = (segments: SegmentFeature[]): CoverageCell[] => {
  const cellMap = new Map<string, SegmentFeature[]>();

  for (const segment of segments) {
    const rawCoords = segment.geometry?.coordinates?.[0];
    if (!rawCoords || rawCoords.length === 0) continue;

    const points: { lat: number; lng: number }[] = [];
    for (const coord of rawCoords) {
      const lng = Number(coord[0]);
      const lat = Number(coord[1]);
      if (!isFinite(lat) || !isFinite(lng) || lat === 0 || lng === 0) continue;
      points.push({ lat, lng });
    }
    if (points.length === 0) continue;

    // Se marcan solo las celdas que toca el contorno del tramo, no su bounding box.
    // Para tramos urbanos (~50 m) el resultado es el mismo; para rutas rurales de
    // varios km (ej. Ruta 5 en Antofagasta o Ruta B151 en Calama) evita pintar un
    // rectángulo completo entre ambos extremos.
    const touchedCells = new Set<string>();
    const markCell = (lat: number, lng: number) => {
      touchedCells.add(`${Math.floor(lng / CELL_SIZE_LNG)}|${Math.floor(lat / CELL_SIZE_LAT)}`);
    };

    for (let i = 0; i < points.length; i++) {
      const from = points[i];
      const to = points[(i + 1) % points.length];
      const spanCells = Math.max(
        Math.abs(to.lng - from.lng) / CELL_SIZE_LNG,
        Math.abs(to.lat - from.lat) / CELL_SIZE_LAT
      );
      const steps = Math.max(1, Math.ceil(spanCells * 2));
      for (let s = 0; s <= steps; s++) {
        const t = s / steps;
        markCell(from.lat + (to.lat - from.lat) * t, from.lng + (to.lng - from.lng) * t);
      }
    }

    touchedCells.forEach(key => {
      const bucket = cellMap.get(key);
      if (bucket) {
        bucket.push(segment);
      } else {
        cellMap.set(key, [segment]);
      }
    });
  }

  return Array.from(cellMap.entries()).map(([key, cellSegments]) => {
    const [cellX, cellY] = key.split('|').map(Number);
    const cellMinLng = cellX * CELL_SIZE_LNG;
    const cellMaxLng = (cellX + 1) * CELL_SIZE_LNG;
    const cellMinLat = cellY * CELL_SIZE_LAT;
    const cellMaxLat = (cellY + 1) * CELL_SIZE_LAT;

    return {
      key,
      paths: [
        { lat: cellMinLat, lng: cellMinLng },
        { lat: cellMinLat, lng: cellMaxLng },
        { lat: cellMaxLat, lng: cellMaxLng },
        { lat: cellMaxLat, lng: cellMinLng }
      ],
      segments: cellSegments
    };
  });
};

const normalizeComunaName = (value: string): string =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '');

const App: React.FC = () => {
  const [fiberData, setFiberData] = useState<FiberData | null>(null);
  const [selectedSegment, setSelectedSegment] = useState<SegmentFeature | null>(null);
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [mapZoom, setMapZoom] = useState(13);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [showMap, setShowMap] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState<Address | null>(null);
  const [relevantSegments, setRelevantSegments] = useState<SegmentFeature[]>([]);
  const [dataLoadError, setDataLoadError] = useState<string | null>(null);
  const [availableComunas, setAvailableComunas] = useState<string[]>([]);
  const [comunaCache, setComunaCache] = useState<Record<string, FiberData>>({});
  const [comunaSlugMap, setComunaSlugMap] = useState<Record<string, string>>({});

  const mapsApiKey = (process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '').trim();
  const hasInvalidPlaceholderKey = !mapsApiKey || mapsApiKey === 'TU_API_KEY' || mapsApiKey.startsWith('TU_API_KEY');

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: mapsApiKey,
    language: 'es',
    region: 'CL'
  });

  // Load metadata first (lightweight)
  useEffect(() => {
    console.log('Loading metadata...');
    fetch('/datos_metadata.json')
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then((metadata: any) => {
        console.log('Metadata loaded:', metadata);
        const comunas = metadata.comunas.map((c: any) => c.nombre);
        const slugMap = metadata.comunas.reduce((acc: Record<string, string>, c: any) => {
          acc[normalizeComunaName(c.nombre)] = c.slug;
          return acc;
        }, {});
        setAvailableComunas(comunas);
        setComunaSlugMap(slugMap);
        setDataLoadError(null);
      })
      .catch(error => {
        console.error('Error loading metadata:', error);
        setDataLoadError('No se pudo cargar la lista de comunas.');
      });
  }, []);

  // Function to load a specific comuna's data
  const loadComuna = async (comunaSlug: string): Promise<FiberData | null> => {
    if (comunaCache[comunaSlug]) {
      console.log(`Comuna ${comunaSlug} already loaded`);
      return comunaCache[comunaSlug];
    }

    console.log(`Loading comuna: ${comunaSlug}...`);

    try {
      const response = await fetch(`/comunas/${comunaSlug}.json`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const comunaData = await response.json();
      console.log(`Comuna ${comunaSlug} loaded:`, {
        segments: comunaData.segmentos.features.length,
        addresses: comunaData.direcciones.length
      });

      // Merge data with existing fiberData
      setFiberData(prevData => {
        if (!prevData) {
          // First comuna loaded
          return comunaData;
        }

        // Merge segments and addresses
        return {
          ...prevData,
          segmentos: {
            type: 'FeatureCollection',
            features: [
              ...prevData.segmentos.features,
              ...comunaData.segmentos.features
            ]
          },
          direcciones: [
            ...prevData.direcciones,
            ...comunaData.direcciones
          ],
          metadata: {
            ...prevData.metadata,
            comunas: [...new Set([...(prevData.metadata.comunas || []), comunaData.metadata.comuna])]
          }
        };
      });

      setComunaCache(prev => ({ ...prev, [comunaSlug]: comunaData }));
      return comunaData;
    } catch (error) {
      console.error(`Error loading comuna ${comunaSlug}:`, error);
      // Don't set global error, just log it
      return null;
    }
  };

  const onLoad = useCallback((map: google.maps.Map) => {
    setMap(map);
  }, []);

  const onUnmount = useCallback(() => {
    setMap(null);
  }, []);

  // DEBUG: Show some polygons for testing
  useEffect(() => {
    if (fiberData && relevantSegments.length === 0 && showMap) {
      console.warn('Map is showing but no relevant segments! This is the issue.');
      console.log('FiberData has', fiberData.segmentos.features.length, 'total segments');
    }
  }, [fiberData, relevantSegments, showMap]);

  const handleComunaSearch = async (comunaCode: string) => {
    const normalizedComuna = normalizeComunaName(comunaCode);
    const comunaSlug = comunaSlugMap[normalizedComuna] || comunaCode.toLowerCase().replace(/\s+/g, '-');

    const loadedData = await loadComuna(comunaSlug);
    const targetComunaName = comunaDisplayName(comunaCode);
    const targetNormalized = normalizeComunaName(comunaCode);

    // Cada JSON contiene una sola comuna, así que no se filtra por nombre: en los
    // datasets históricos el nombre del archivo (SERENA, SNMIGEL) no coincide con
    // properties.comuna (LA SERENA, SAN MIGUEL) y el filtro dejaba el mapa vacío.
    // Solo si la carga falló se recurre a lo ya acumulado, filtrando por comuna.
    const candidateSegments: SegmentFeature[] = loadedData
      ? loadedData.segmentos.features
      : (fiberData?.segmentos?.features || []).filter(
          s => normalizeComunaName(s.properties.comuna) === targetNormalized
        );
    const comunaSegments = candidateSegments.filter(s => s.properties.disponibilidad.fibra_disponible);

    if (comunaSegments.length === 0) {
      // Se muestra igual el mapa: el overlay "No se encontraron zonas con cobertura"
      // le da feedback al usuario en vez de dejar el formulario sin reacción.
      console.warn(`No se encontraron zonas con cobertura para ${targetComunaName}`);
    }

    const centerTuple = loadedData?.configuracion_mapa?.centro;
    const mapPosition = centerTuple && centerTuple.length === 2
      ? { lat: Number(centerTuple[0]), lng: Number(centerTuple[1]) }
      : defaultCenter;

    setSelectedAddress({
      id: null,
      direccion_completa: `Cobertura comunal - ${targetComunaName}`,
      calle: '',
      numero: 0,
      comuna: targetComunaName,
      coordenadas: [mapPosition.lng, mapPosition.lat],
      segmento_id: null,
      tiene_cobertura_corp: comunaSegments.length > 0
    });
    setRelevantSegments(comunaSegments);
    setSelectedSegment(comunaSegments.length > 0 ? comunaSegments[0] : null);
    setMapCenter(mapPosition);
    setMapZoom(13);
    
    setShowMap(true);

    setTimeout(() => {
      if (map) {
        map.panTo(mapPosition);
        map.setZoom(13);
      }
    }, 400);
  };

  const handlePolygonClick = (segment: SegmentFeature) => {
    setSelectedSegment(segment);
  };

  const handleBackToForm = () => {
    setShowMap(false);
    setSelectedAddress(null);
    setRelevantSegments([]);
    setSelectedSegment(null);
  };

  const coverageCells = useMemo(
    () => aggregateSegmentsToCells(relevantSegments),
    [relevantSegments]
  );

  const getCellPolygonOptions = (cell: CoverageCell): google.maps.PolygonOptions => {
    const selectedId = selectedSegment?.properties.id_segmento;
    const isSelected = selectedId !== undefined &&
      cell.segments.some(s => s.properties.id_segmento === selectedId);

    return {
      fillColor: '#10B981',
      fillOpacity: isSelected ? 0.68 : 0.55,
      strokeColor: isSelected ? '#0066CC' : '#10B981',
      strokeOpacity: isSelected ? 1 : 0,
      strokeWeight: isSelected ? 4 : 0,
      clickable: true,
      draggable: false,
      editable: false,
      geodesic: false,
      zIndex: isSelected ? 10 : 2
    };
  };

  if (loadError) {
    return (
      <div className="error-container">
        <h2>Error al cargar Google Maps</h2>
        <p>Por favor, verifica tu API key de Google Maps</p>
      </div>
    );
  }

  if (hasInvalidPlaceholderKey) {
    return (
      <div className="flex flex-col justify-center items-center bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 min-h-screen">
        <div className="bg-white shadow-2xl mx-4 p-8 sm:p-12 rounded-3xl max-w-2xl text-center">
          <div className="mb-6 text-6xl">🗺️</div>
          <h2 className="mb-4 font-bold text-slate-900 text-2xl sm:text-3xl">API Key de Google Maps no configurada</h2>
          <p className="mb-6 text-slate-600 text-base sm:text-lg leading-relaxed">
            El valor actual es un placeholder. Configura una clave real en <code className="bg-slate-200 px-2 py-1 rounded font-mono text-sm">.env</code>.
          </p>
          <div className="bg-slate-50 p-4 rounded-lg text-left">
            <p className="mb-2 font-semibold text-slate-700 text-sm">Ejemplo:</p>
            <code className="bg-slate-200 px-2 py-1 rounded font-mono text-xs block">
              REACT_APP_GOOGLE_MAPS_API_KEY=AIza...
            </code>
          </div>
        </div>
      </div>
    );
  }

  if (dataLoadError) {
    return (
      <div className="flex flex-col justify-center items-center bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 min-h-screen">
        <div className="bg-white shadow-2xl mx-4 p-8 sm:p-12 rounded-3xl max-w-2xl text-center">
          <div className="flex justify-center mb-6">
            <img
              src="/logo_entel.png"
              alt="Entel"
              className="drop-shadow-md h-16 sm:h-20 object-contain"
            />
          </div>
          <div className="mb-6 text-6xl">📊</div>
          <h2 className="mb-4 font-bold text-slate-900 text-2xl sm:text-3xl">Dataset No Disponible</h2>
          <p className="mb-6 text-slate-600 text-base sm:text-lg leading-relaxed">{dataLoadError}</p>
          <div className="bg-slate-50 p-4 rounded-lg text-left">
            <p className="mb-2 font-semibold text-slate-700 text-sm">Para cargar el dataset:</p>
            <ol className="space-y-2 text-slate-600 text-sm list-decimal list-inside">
              <li>Coloca el archivo <code className="bg-slate-200 px-2 py-1 rounded font-mono text-xs">datos_multiples_comunas.json</code> en la carpeta <code className="bg-slate-200 px-2 py-1 rounded font-mono text-xs">public/</code></li>
              <li>Recarga la página</li>
            </ol>
          </div>
        </div>
      </div>
    );
  }

  // Show loading only if Google Maps is loading
  if (!isLoaded) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Cargando Google Maps...</p>
      </div>
    );
  }

  // Mostrar formulario si no se ha hecho búsqueda
  if (!showMap) {
    return (
      <AddressFormTailwind
        fiberData={fiberData}
        onSearchComuna={handleComunaSearch}
        availableComunas={availableComunas}
      />
    );
  }

  // Mostrar mapa después de búsqueda
  return (
    <div className="relative w-full h-screen overflow-hidden">
      {/* Header with search info */}
      <div className="top-0 right-0 left-0 z-[999] absolute flex sm:flex-row flex-col items-start sm:items-center gap-3 sm:gap-6 bg-white shadow-md px-4 sm:px-6 py-3 sm:py-4">
        <button 
          type="button" 
          onClick={handleBackToForm}
          className="flex items-center gap-2 bg-slate-100 hover:bg-slate-200 px-4 py-2 border border-slate-300 rounded-lg font-semibold text-slate-700 text-sm transition-all duration-200"
        >
          <span>←</span>
          <span>Nueva consulta</span>
        </button>
        {selectedAddress && (
          <div className="flex-1">
            <h2 className="mb-1 font-bold text-slate-900 text-base sm:text-lg lg:text-xl">
              {selectedAddress.direccion_completa}
            </h2>
          </div>
        )}
      </div>

      {/* Google Map */}
      <div className="top-16 sm:top-20 right-0 bottom-0 left-0 absolute w-full">
        {relevantSegments.length === 0 && (
          <div className="top-1/2 left-1/2 z-[999] absolute bg-white shadow-xl px-6 py-5 rounded-xl text-center -translate-x-1/2 -translate-y-1/2 transform">
            <p className="mb-2 font-semibold text-slate-900 text-base">No se encontraron zonas con cobertura</p>
            <p className="text-slate-600 text-sm">Intenta otra comuna</p>
          </div>
        )}
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={mapCenter}
          zoom={mapZoom}
          onLoad={onLoad}
          onUnmount={onUnmount}
          options={{
            streetViewControl: false,
            mapTypeControl: true,
            fullscreenControl: true,
            zoomControl: true,
            zoomControlOptions: {
              position: 8 // RIGHT_CENTER
            },
            mapTypeControlOptions: {
              position: 2 // TOP_RIGHT
            },
            fullscreenControlOptions: {
              position: 7 // RIGHT_BOTTOM
            },
            disableDefaultUI: false,
            styles: [
              {
                featureType: 'poi',
                elementType: 'labels',
                stylers: [{ visibility: 'off' }]
              },
              {
                featureType: 'transit',
                elementType: 'all',
                stylers: [{ visibility: 'off' }]
              },
              // Suavizar colores generales del mapa
              {
                featureType: 'all',
                elementType: 'all',
                stylers: [{ saturation: -20 }, { lightness: 10 }]
              },
              // Suavizar carreteras
              {
                featureType: 'road',
                elementType: 'all',
                stylers: [{ saturation: -30 }, { lightness: 20 }]
              },
              // Suavizar edificios
              {
                featureType: 'landscape.man_made',
                elementType: 'all',
                stylers: [{ saturation: -25 }, { lightness: 15 }]
              },
              // Suavizar áreas verdes
              {
                featureType: 'landscape.natural',
                elementType: 'all',
                stylers: [{ saturation: -15 }, { lightness: 5 }]
              }
            ]
          }}
        >
          {/* Render aggregated coverage cells */}
          <>
            {coverageCells.map(cell => (
              <Polygon
                key={`cell-${cell.key}`}
                paths={cell.paths}
                options={getCellPolygonOptions(cell)}
                onClick={() => handlePolygonClick(cell.segments[0])}
              />
            ))}
          </>

        </GoogleMap>
      </div>

      {/* Side Panel */}
      <SidePanelTailwind />
    </div>
  );
};

export default App;

